import theme from '../../theme';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LEARN_SUBDOMAIN_URL } from '../../lib/subdomain';

const LANDING_CSS = `
  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
    gap: 24px;
    margin: 48px 0;
    text-align: left;
  }
  .feature-tile {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: ${theme.bgCard};
    border: 1px solid rgba(184, 149, 90, 0.25);
    border-radius: 4px;
    padding: 28px 24px;
    text-decoration: none;
    color: inherit;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    position: relative;
    overflow: hidden;
  }
  .feature-tile::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${theme.gold};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  .feature-tile:hover::before {
    transform: scaleX(1);
  }
  .feature-tile:hover {
    transform: translateY(-5px);
    border-color: rgba(184, 149, 90, 0.5);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
  }
  .tile-number {
    font-family: 'Josefin Sans', sans-serif;
    font-weight: 400;
    font-size: 12px;
    letter-spacing: 0.15em;
    color: ${theme.gold};
    text-transform: uppercase;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .tile-title {
    font-family: 'EB Garamond', serif;
    font-size: 22px;
    font-weight: 400;
    color: ${theme.textPrimary};
    letter-spacing: 0.02em;
    margin-bottom: 12px;
    line-height: 1.25;
    transition: color 0.2s ease;
  }
  .feature-tile:hover .tile-title {
    color: ${theme.gold};
  }
  .tile-desc {
    font-family: 'EB Garamond', serif;
    font-size: 16px;
    color: ${theme.textMuted};
    line-height: 1.6;
    margin-bottom: 24px;
    flex-grow: 1;
  }
  .tile-action {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #7a8a9a;
    transition: color 0.2s ease;
  }
  .feature-tile:hover .tile-action {
    color: ${theme.gold};
  }
  .tile-arrow {
    transition: transform 0.2s ease;
  }
  .feature-tile:hover .tile-arrow {
    transform: translateX(4px);
  }
  .conversation-button {
    display: inline-block;
    background: transparent;
    border: 1px solid rgba(184, 149, 90, 0.6);
    color: ${theme.gold};
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    padding: 10px 24px;
    border-radius: 2px;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    margin-top: 8px;
    margin-bottom: 8px;
  }
  .conversation-button:hover {
    background: rgba(184, 149, 90, 0.05);
    border-color: ${theme.gold};
    color: ${theme.textPrimary};
  }

  /* The Diploma band. Uses --ap-gold-rgb rather than the literal 184,149,90
     used elsewhere in this file, so it stays correct in light mode too. */
  .diploma-band {
    max-width: 700px;
    margin: 0 auto 48px;
    padding: 32px;
    text-align: left;
    border: 1px solid rgba(var(--ap-gold-rgb), 0.4);
    border-radius: 2px;
    background: rgba(var(--ap-gold-rgb), 0.05);
  }
  .diploma-eyebrow {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${theme.gold};
    margin: 0 0 12px;
  }
  .diploma-title {
    font-family: 'EB Garamond', serif;
    font-size: 27px;
    font-weight: 400;
    color: ${theme.textPrimary};
    margin: 0 0 14px;
    text-wrap: balance;
  }
  .diploma-desc {
    font-family: 'EB Garamond', serif;
    font-size: 16.5px;
    color: ${theme.textMuted};
    line-height: 1.65;
    margin: 0 0 20px;
  }
  .diploma-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 20px;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin: 0 0 24px;
    padding: 0;
    list-style: none;
  }
  .diploma-meta strong {
    color: ${theme.gold};
    font-weight: 400;
    font-variant-numeric: tabular-nums;
  }
  .diploma-cta {
    display: inline-block;
    background: ${theme.gold};
    color: ${theme.bgDark};
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    padding: 12px 26px;
    border-radius: 2px;
    text-decoration: none;
    transition: opacity 0.2s ease;
  }
  .diploma-cta:hover { opacity: 0.85; }
  .diploma-aside {
    font-family: 'EB Garamond', serif;
    font-size: 15px;
    color: ${theme.textMuted};
    margin: 16px 0 0;
  }
`;

const features = [
  {
    to: '/process',
    category: 'PHILOSOPHY',
    title: 'Why Austrian',
    description: "What it shows: A critique of mainstream equilibrium models and a defense of subjectivism, time preference, and capital structure. Why it matters: Explains why mathematical models fail to explain real-world coordination. How to use it: Read the structured essay to explore Mises, Hayek, and Lachmann's core arguments. What to notice: Notice the distinction between real savings-driven growth and credit-induced expansion.",
  },
  {
    to: '/garrison-triangle',
    category: 'MODEL',
    title: 'The Garrison Triangle',
    description: 'What it shows: How consumption and investment decisions are coordinated across stages of production over time. Why it matters: Illustrates how interest rate manipulations corrupt price signals, causing malinvestments. How to use it: Adjust the time preference slider to shift resource allocation from consumption to early-stage capital production. What to notice: Notice how artificial credit expansions create structural conflicts between saver preferences and builder plans.',
  },
  {
    to: '/fred',
    category: 'DATA',
    title: 'Interest Rates',
    description: 'What it shows: The Federal Funds Rate alongside historical cycles of central bank credit expansion. Why it matters: Demonstrates how interest rate suppression distorts price signals. How to use it: Toggle different historical recession cycles to view rate movements. What to notice: Notice how rate cuts systematically precede credit-induced business cycles.',
  },
  {
    to: '/calculation-problem',
    category: 'PLANNER',
    title: 'The Calculation Problem',
    description: "What it shows: A central planning simulator where the user attempts to allocate capital goods without price signals. Why it matters: Demonstrates Mises's thesis that rational allocation is impossible without property rights and prices. How to use it: Set production targets for industries and try to coordinate supply chains manually. What to notice: Notice how rapidly shortages and surpluses emerge when pricing information is removed.",
  },
  {
    to: '/ask-austrian',
    category: 'AI ASSISTANT',
    title: 'Ask Austrian',
    description: 'What it shows: An economic research assistant trained to respond strictly using Austrian literature. Why it matters: Grounded in verified texts to prevent generic AI hallucinations and outcomes claims. How to use it: Type theoretical queries on capital structures, monetary theory, or historical debates. What to notice: Notice the detailed citations and refusal to provide investment or security advice.',
  },
  {
    to: '/quiz',
    category: 'TEST',
    title: 'The Quiz',
    description: 'What it shows: Randomized conceptual testing on the business cycle, spontaneous order, and sound money. Why it matters: Helps solidify key economics terminology and structural theory. How to use it: Answer ten multiple-choice questions to receive a scorecard and conceptual explanations. What to notice: Notice the precision of vocabulary required to analyze subjective human action.',
  },
  {
    to: '/shelf',
    category: 'LIBRARY',
    title: 'The Shelf',
    description: 'What it shows: Curated library of foundational texts by Mises, Hayek, Lachmann, and Rothbard. Why it matters: Acts as the conceptual foundation for all the other tools on the site. How to use it: Browse books by thinker, reading guides, and direct text summaries. What to notice: Notice the common thread of subjectivism, time alignment, and coordinate process.',
  },
  {
    to: '/explainers',
    category: 'EXPLAINERS',
    title: 'Explainers',
    description: 'What it shows: Conceptual briefs translating Austrian theory into plain-language explainers. Why it matters: Connects abstract economic concepts to real-world decisions and events. How to use it: Browse the list of guides covering prices, time preference, and central planning. What to notice: Notice the clear emphasis on coordination, structure, and long-term thinking over short-term forecasting.',
  },
  {
    to: '/reading-notes',
    category: 'NOTES',
    title: 'Reading Notes',
    description: 'What it shows: In-depth analyses of foundational texts with key summaries and why they still matter. Why it matters: Grounded study of books like Human Action and Prices and Production. How to use it: Review core ideas, thinker guides, and related tools. What to notice: Notice the analytical frameworks used to dissect credit-driven cycles.',
  },
];

function LandingPage() {
  useEffect(() => {
    document.title = "Austrian Process - Austrian Economics, Applied";
  }, []);

  return (
    <div className="page-container" style={{ textAlign: 'center' }}>
      <style>{LANDING_CSS}</style>

      <h1 style={{ fontSize: '40px', marginBottom: '8px', color: theme.textPrimary, fontFamily: "'EB Garamond', serif", fontWeight: '400', letterSpacing: '0.02em' }}>Austrian Process</h1>
      <p style={{ fontSize: '18px', fontStyle: 'italic', color: theme.gold, marginBottom: '32px', fontFamily: "'EB Garamond', serif" }}>
        Austrian Economics, applied.
      </p>

      <p style={{ fontSize: '16px', color: 'rgba(var(--ap-text-primary-rgb), 0.8)', maxWidth: '560px', margin: '0 auto 48px', lineHeight: '1.7', textAlign: 'center' }}>
        Austrian Economics explains how economies grow, why booms become busts, and why
        central planning fails. Through the lens of individual choice and time. These tools
        make those ideas tangible.
      </p>

      {/* The structured path. Sits above the onboarding guide because it is the
          deepest thing on offer -- the guide below is the browse-freely route
          for people who don't want to commit to a program. Cross-origin, so a
          plain anchor rather than a react-router <Link>. */}
      <section className="diploma-band">
        <p className="diploma-eyebrow">The Program</p>
        <h2 className="diploma-title">Diploma in Austrian Economics</h2>
        <p className="diploma-desc">
          A self-paced course through the Austrian School, taken in order and ending in an
          examined credential rather than a certificate of attendance. You finish by defending
          an argument in writing and sitting a comprehensive exam across all three levels.
        </p>
        <ul className="diploma-meta">
          <li><strong>3</strong> Levels</li>
          <li><strong>32</strong> Lessons</li>
          <li><strong>Essay</strong> + Exam</li>
          <li><strong>Free</strong></li>
        </ul>
        <a className="diploma-cta" href={LEARN_SUBDOMAIN_URL}>View the Diploma &rarr;</a>
        <p className="diploma-aside">
          Prefer to explore first? The guide below walks through the tools in a sensible order.
        </p>
      </section>

      {/* "Start Here" Onboarding Section */}
      <div className="onboarding-card" style={{
        backgroundColor: theme.bgDark,
        border: '1px solid rgba(184, 149, 90, 0.25)',
        outline: '1px solid rgba(184, 149, 90, 0.15)',
        outlineOffset: '4px',
        padding: '36px 32px',
        maxWidth: '700px',
        margin: '0 auto 48px',
        textAlign: 'left',
        borderRadius: '2px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)'
      }}>
        <div style={{
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '12px',
          fontWeight: '400',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: theme.gold,
          marginBottom: '12px',
          borderBottom: '1px solid rgba(184, 149, 90, 0.2)',
          paddingBottom: '8px'
        }}>
          Start Here: Onboarding Guide
        </div>
        <h2 style={{
          fontFamily: "'EB Garamond', serif",
          fontSize: '24px',
          color: theme.textPrimary,
          marginBottom: '16px',
          fontWeight: '400'
        }}>Introduction to the Austrian Process</h2>
        <p style={{
          fontFamily: "'EB Garamond', serif",
          fontSize: '16.5px',
          color: theme.textMuted,
          lineHeight: '1.6',
          marginBottom: '24px'
        }}>
          This platform translates the theoretical insights of the Austrian School of Economics into interactive models and data dashboards. Grounded in the works of Ludwig von Mises, Friedrich Hayek, and Ludwig Lachmann, these tools demonstrate how human actions, time preference, and price signals coordinate capital production. We recommend exploring them in this progression:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <span style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '12px',
              fontWeight: '400',
              color: theme.gold,
              background: 'rgba(184, 149, 90, 0.1)',
              border: '1px solid rgba(184, 149, 90, 0.3)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>1</span>
            <div>
              <strong style={{ color: theme.textPrimary, fontFamily: "'EB Garamond', serif", fontSize: '17px', fontWeight: '500' }}>Read Why Austrian</strong>
              <p style={{ margin: '4px 0 0', fontFamily: "'EB Garamond', serif", fontSize: '15px', color: theme.textMuted, lineHeight: '1.5' }}>
                Understand the case against mainstream equilibrium models in the <Link to="/process" style={{ color: theme.gold, textDecoration: 'underline', textUnderlineOffset: '3px' }}>Why Austrian</Link> essay.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <span style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '12px',
              fontWeight: '400',
              color: theme.gold,
              background: 'rgba(184, 149, 90, 0.1)',
              border: '1px solid rgba(184, 149, 90, 0.3)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>2</span>
            <div>
              <strong style={{ color: theme.textPrimary, fontFamily: "'EB Garamond', serif", fontSize: '17px', fontWeight: '500' }}>Explore the Garrison Triangle</strong>
              <p style={{ margin: '4px 0 0', fontFamily: "'EB Garamond', serif", fontSize: '15px', color: theme.textMuted, lineHeight: '1.5' }}>
                Interact with the <Link to="/garrison-triangle" style={{ color: theme.gold, textDecoration: 'underline', textUnderlineOffset: '3px' }}>Garrison Triangle</Link> model to visualize how consumption and investment decisions coordinate over time.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <span style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '12px',
              fontWeight: '400',
              color: theme.gold,
              background: 'rgba(184, 149, 90, 0.1)',
              border: '1px solid rgba(184, 149, 90, 0.3)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>3</span>
            <div>
              <strong style={{ color: theme.textPrimary, fontFamily: "'EB Garamond', serif", fontSize: '17px', fontWeight: '500' }}>Try the Calculation Problem</strong>
              <p style={{ margin: '4px 0 0', fontFamily: "'EB Garamond', serif", fontSize: '15px', color: theme.textMuted, lineHeight: '1.5' }}>
                Test your resource allocation coordination limits in the <Link to="/calculation-problem" style={{ color: theme.gold, textDecoration: 'underline', textUnderlineOffset: '3px' }}>Calculation Problem</Link> simulator.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <span style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '12px',
              fontWeight: '400',
              color: theme.gold,
              background: 'rgba(184, 149, 90, 0.1)',
              border: '1px solid rgba(184, 149, 90, 0.3)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>4</span>
            <div>
              <strong style={{ color: theme.textPrimary, fontFamily: "'EB Garamond', serif", fontSize: '17px', fontWeight: '500' }}>Test yourself with the Quiz</strong>
              <p style={{ margin: '4px 0 0', fontFamily: "'EB Garamond', serif", fontSize: '15px', color: theme.textMuted, lineHeight: '1.5' }}>
                Assess your knowledge of cycles, pricing systems, and capital theory in <Link to="/quiz" style={{ color: theme.gold, textDecoration: 'underline', textUnderlineOffset: '3px' }}>The Quiz</Link>.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <span style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '12px',
              fontWeight: '400',
              color: theme.gold,
              background: 'rgba(184, 149, 90, 0.1)',
              border: '1px solid rgba(184, 149, 90, 0.3)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>5</span>
            <div>
              <strong style={{ color: theme.textPrimary, fontFamily: "'EB Garamond', serif", fontSize: '17px', fontWeight: '500' }}>Ask Austrian</strong>
              <p style={{ margin: '4px 0 0', fontFamily: "'EB Garamond', serif", fontSize: '15px', color: theme.textMuted, lineHeight: '1.5' }}>
                Query the economic economic research assistant <Link to="/ask-austrian" style={{ color: theme.gold, textDecoration: 'underline', textUnderlineOffset: '3px' }}>Ask Austrian</Link> to resolve conceptual or history questions.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <span style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '12px',
              fontWeight: '400',
              color: theme.gold,
              background: 'rgba(184, 149, 90, 0.1)',
              border: '1px solid rgba(184, 149, 90, 0.3)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>6</span>
            <div>
              <strong style={{ color: theme.textPrimary, fontFamily: "'EB Garamond', serif", fontSize: '17px', fontWeight: '500' }}>Browse The Shelf</strong>
              <p style={{ margin: '4px 0 0', fontFamily: "'EB Garamond', serif", fontSize: '15px', color: theme.textMuted, lineHeight: '1.5' }}>
                Consult foundational summaries and thinkers list in <Link to="/shelf" style={{ color: theme.gold, textDecoration: 'underline', textUnderlineOffset: '3px' }}>The Shelf</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="features-grid">
        {features.map(({ to, category, title, description }) => (
          <Link key={to} to={to} className="feature-tile">
            <div>
              <div className="tile-number">
                <span>{category}</span>
              </div>
              <h3 className="tile-title">{title}</h3>
              <p className="tile-desc">{description}</p>
            </div>
            <div className="tile-action">
              <span>Explore</span>
              <span className="tile-arrow">→</span>
            </div>
          </Link>
        ))}
      </div>

    </div>
  );
}

export default LandingPage;
